import { useCallback, useRef, useState } from 'react';

/**
 * useBatchUpload — calm, resilient batch upload coordinator.
 *
 * Drives any "drop N files, upload them, never lie about the result" UX.
 * Used by both the studio (Board editor + Library) and the client (board
 * share view inspirations).
 *
 * Guarantees:
 *   1. Bounded concurrency (default 5) so we never fire 555 requests at once.
 *   2. One automatic retry per failed file. If it still fails, it stays
 *      failed and surfaces in the summary.
 *   3. The "uploading" flag ALWAYS resolves to false when every file has
 *      reached a terminal state (done | failed). No stuck spinners.
 *   4. Successful uploads are never rolled back — partial failures don't
 *      kill the batch.
 *   5. Per-file diagnostic capture: filename, size, mime, HTTP status,
 *      structured backend error code, raw detail string, step where it
 *      failed (upload | retry).
 *
 * Usage
 * -----
 *   const batch = useBatchUpload({
 *     uploadFn: async (file) => clientAPI.uploadInspiration(...),
 *     concurrency: 5,
 *     onAllDone: ({ done, failed }) => { ... },
 *   });
 *   batch.start(files);                  // begin
 *   batch.retryFailed();                 // re-run only the failures
 *   batch.dismiss();                     // reset summary panel
 *
 *   batch.state = {
 *     uploading: boolean,                // false when terminal
 *     total: number,                     // total files in this batch
 *     doneCount: number,
 *     failedCount: number,
 *     remainingCount: number,
 *     summaryOpen: boolean,              // show the final summary card?
 *     items: [
 *       { id, file, status, errorCode, errorMessage, errorStatus,
 *         attempts, sizeBytes, mime }
 *     ]
 *   }
 */
export const useBatchUpload = ({ uploadFn, concurrency = 5, onAllDone, onProgress } = {}) => {
  const [state, setState] = useState({
    uploading: false,
    total: 0,
    doneCount: 0,
    failedCount: 0,
    remainingCount: 0,
    summaryOpen: false,
    items: [],
  });

  // Ref mirror so the worker pool can read fresh state without re-creating
  // the closure on every render.
  const itemsRef = useRef([]);
  const cancelRef = useRef(false);

  // Per-batch identity. Every call to start() / retryFailed() / dismiss()
  // bumps this counter. Workers receive the batchId active when they
  // were spawned and self-cancel before writing into itemsRef / state if
  // the value has advanced — this prevents an in-flight batch from
  // corrupting a newer batch's counts when the user spam-clicks upload.
  const batchIdRef = useRef(0);

  // ── Internal: run a single item, with one auto-retry on failure ─────
  const runOne = useCallback(async (idx, myBatchId) => {
    // Old workers from a previous batch must not touch the new state.
    if (myBatchId !== batchIdRef.current) return;
    const item = itemsRef.current[idx];
    if (!item || item.status === 'done') return;
    for (let attempt = 1; attempt <= 2; attempt++) {
      if (cancelRef.current || myBatchId !== batchIdRef.current) return;
      itemsRef.current[idx] = { ...item, status: 'uploading', attempts: attempt };
      try {
        // Pass the original selection position so the upload endpoint can
        // pin the image's relative order even though uploads run with
        // concurrency=5. Without this, the upload-completion order races
        // and the board sequence flickers from the photographer's intent.
        await uploadFn(item.file, idx);
        // Recheck after the network round-trip: by the time we resume,
        // a newer batch may already own this slot.
        if (myBatchId !== batchIdRef.current) return;
        itemsRef.current[idx] = {
          ...itemsRef.current[idx],
          status: 'done',
          errorCode: null,
          errorMessage: null,
          errorStatus: null,
        };
        return;
      } catch (err) {
        if (myBatchId !== batchIdRef.current) return;
        // Classify the failure so the summary card shows a structured
        // code instead of a raw axios message. Backend errors arrive
        // as { detail: { code, message } } and are unpacked below.
        // Axios client-side timeouts (no response, err.code === 'ECONNABORTED')
        // are translated into our own 'upload_timeout' code so the
        // BatchUploadStatus failure list groups them naturally and the
        // Retry button picks them up exactly like any other failure.
        const detail = err?.response?.data?.detail;
        const httpStatus = err?.response?.status || 0;
        const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
        let code = 'unknown';
        let message = err?.message || 'Upload failed';
        if (isTimeout) {
          code = 'upload_timeout';
          message = 'Upload timed out';
        } else if (detail && typeof detail === 'object') {
          code = detail.code || 'unknown';
          message = detail.message || message;
        } else if (typeof detail === 'string') {
          message = detail;
        }
        const nonRetriable = ['inspirations_full', 'invalid_mime', 'storage_full', 'board_locked', 'uploads_disabled', 'board_not_found'];
        const shouldRetry = attempt === 1 && !nonRetriable.includes(code) && httpStatus !== 400 && httpStatus !== 403 && httpStatus !== 404;
        itemsRef.current[idx] = {
          ...itemsRef.current[idx],
          status: shouldRetry ? 'queued' : 'failed',
          errorCode: code,
          errorMessage: message,
          errorStatus: httpStatus,
          attempts: attempt,
        };
        if (!shouldRetry) return;
        // brief backoff before the single retry
        await new Promise((r) => setTimeout(r, 350));
        if (myBatchId !== batchIdRef.current) return;
      }
    }
  }, [uploadFn]);

  // ── Internal: drive a pool of `concurrency` workers ─────────────────
  const drainQueue = useCallback(async (indices, myBatchId) => {
    let cursor = 0;
    const next = async () => {
      while (cursor < indices.length) {
        if (cancelRef.current || myBatchId !== batchIdRef.current) return;
        const myIdx = indices[cursor++];
        await runOne(myIdx, myBatchId);
        if (myBatchId !== batchIdRef.current) return;
        // Push a fresh snapshot of counts after each item resolves
        const fresh = itemsRef.current;
        const done = fresh.filter((i) => i.status === 'done').length;
        const failed = fresh.filter((i) => i.status === 'failed').length;
        const total = fresh.length;
        setState((prev) => ({
          ...prev,
          items: [...fresh],
          doneCount: done,
          failedCount: failed,
          remainingCount: total - done - failed,
        }));
        if (onProgress) onProgress({ done, failed, total, remaining: total - done - failed });
      }
    };
    const workers = Array.from({ length: Math.max(1, concurrency) }, () => next());
    await Promise.all(workers);
  }, [runOne, concurrency, onProgress]);

  // ── Public: start a fresh batch ─────────────────────────────────────
  const start = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    cancelRef.current = false;
    // Take ownership of a new batch. Any prior workers still draining
    // will observe the bumped id and bail before mutating state.
    const myBatchId = ++batchIdRef.current;
    const initial = Array.from(files).map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      filename: file.name,
      sizeBytes: file.size,
      mime: file.type || 'application/octet-stream',
      status: 'queued',
      errorCode: null,
      errorMessage: null,
      errorStatus: null,
      attempts: 0,
    }));
    itemsRef.current = initial;
    setState({
      uploading: true,
      total: initial.length,
      doneCount: 0,
      failedCount: 0,
      remainingCount: initial.length,
      summaryOpen: false,
      items: [...initial],
    });
    const allIdx = initial.map((_, i) => i);
    await drainQueue(allIdx, myBatchId);
    // If a newer batch (or dismiss) has superseded us, do NOT overwrite
    // the current state with this batch's terminal snapshot.
    if (myBatchId !== batchIdRef.current) return;
    const finalItems = itemsRef.current;
    const done = finalItems.filter((i) => i.status === 'done').length;
    const failed = finalItems.filter((i) => i.status === 'failed').length;
    setState({
      uploading: false,
      total: finalItems.length,
      doneCount: done,
      failedCount: failed,
      remainingCount: 0,
      summaryOpen: failed > 0 || finalItems.length > 0,
      items: [...finalItems],
    });
    if (onAllDone) onAllDone({ done, failed, total: finalItems.length, items: finalItems });
  }, [drainQueue, onAllDone]);

  // ── Public: retry only the failed items ─────────────────────────────
  const retryFailed = useCallback(async () => {
    const failedIdx = itemsRef.current
      .map((it, i) => (it.status === 'failed' ? i : -1))
      .filter((i) => i >= 0);
    if (failedIdx.length === 0) return;
    // Reset their status so runOne will pick them up
    failedIdx.forEach((i) => {
      itemsRef.current[i] = {
        ...itemsRef.current[i],
        status: 'queued',
        errorCode: null,
        errorMessage: null,
        errorStatus: null,
        attempts: 0,
      };
    });
    setState((prev) => ({
      ...prev,
      uploading: true,
      summaryOpen: false,
      remainingCount: failedIdx.length,
      items: [...itemsRef.current],
    }));
    cancelRef.current = false;
    // Retry is logically a fresh batch slice — bump the id so any
    // lingering worker from an earlier start() also bails out.
    const myBatchId = ++batchIdRef.current;
    await drainQueue(failedIdx, myBatchId);
    if (myBatchId !== batchIdRef.current) return;
    const finalItems = itemsRef.current;
    const done = finalItems.filter((i) => i.status === 'done').length;
    const failed = finalItems.filter((i) => i.status === 'failed').length;
    setState({
      uploading: false,
      total: finalItems.length,
      doneCount: done,
      failedCount: failed,
      remainingCount: 0,
      summaryOpen: true,
      items: [...finalItems],
    });
    if (onAllDone) onAllDone({ done, failed, total: finalItems.length, items: finalItems });
  }, [drainQueue, onAllDone]);

  // ── Public: dismiss the summary card ────────────────────────────────
  const dismiss = useCallback(() => {
    cancelRef.current = true;
    // Invalidate any workers still mid-flight so they can't restart the
    // summary state after the founder dismissed it.
    batchIdRef.current += 1;
    itemsRef.current = [];
    setState({
      uploading: false,
      total: 0,
      doneCount: 0,
      failedCount: 0,
      remainingCount: 0,
      summaryOpen: false,
      items: [],
    });
  }, []);

  return { state, start, retryFailed, dismiss };
};
